<script setup>
// Imports

// Constants declarations
// Component props
import { getProductDiscount, getProductSalesPrice, getProductSalesPriceWithDiscount, numberFormat } from 'src/utils/utils.js'

const { t } = useI18n({ useScope: 'global' })

const $q = useQuasar()

// Models
const selectedProductModel = defineModel('selectedProduct', { type: [Object, null], required: false })
const selectedExtension = defineModel('selectedExtension', { type: [String, null], required: false })
const domainName = defineModel('domainName', { type: [String, null], required: false })
const isDomainFree = defineModel('isDomainFree', { type: [Boolean, null], required: false })
const isDomainTransferable = defineModel('isDomainTransferable', { type: [Boolean, null], required: false })
const transferCode = defineModel('transferCode', { type: [String, null], required: false })
const freeSuggestedTlds = defineModel('freeSuggestedTlds', { type: [Array], required: false })

// Stores

// Component props
const componentProps = defineProps({
  contract: {
    type: [Object, null],
    required: true,
    default: null,
  },
})

// Constants
const itemsService = 'domains'
const api = useFeathers()

// Refs
const loading = ref(false)
const tldList = ref([])
const domainNameInput = ref(null)
const suggestedTlds = ref(['nl', 'com', 'eu', 'info', 'net', 'vision'])
const suggestedTldsLoading = ref(false)
const searchDomainLoading = ref(false)
const currentSet = ref(0)
const preventSelectedExtensionWatcher = ref(false)

// Computed properties
const itemsPerSet = computed(() => {
  if ($q.screen.xs) return 1
  if ($q.screen.sm) return 2
  if ($q.screen.md) return 3
  return 6 // lg or higher
})

const maxSet = computed(() => Math.ceil(tldList.value.length / itemsPerSet.value) - 1)

const visibleProducts = computed(() => {
  const start = currentSet.value * itemsPerSet.value
  return tldList.value.slice(start, start + itemsPerSet.value)
})

const extensionOptions = computed(() =>
  tldList.value.map((product) => ({
    label: '.' + product.domainTldSettingInfo.tld,
    value: product.domainTldSettingInfo.tld,
  })),
)

const totalSets = computed(() => {
  return Array.from({ length: maxSet.value + 1 }, (_, index) => index)
})

const isValidDomain = computed(() => {
  if ([null, ''].includes(domainNameInput.value)) {
    return true
  }
  const domainRegex = /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)*[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/
  return domainRegex.test(domainNameInput.value)
})

// Methods

// Import functions

// Constants declarations

// Component props

// Store(s)

// Static variables

// Methods
const setCurrentSet = (index) => {
  currentSet.value = index
}

const nextSet = () => {
  if (currentSet.value < maxSet.value) {
    currentSet.value += 1
  }
}

const prevSet = () => {
  if (currentSet.value > 0) {
    currentSet.value -= 1
  }
}

const showCurrentSet = (ext) => {
  if (!ext) {
    return
  }
  const index = tldList.value.findIndex((product) => product.domainTldSettingInfo.tld === ext)
  if (index !== -1) {
    currentSet.value = Math.floor(index / itemsPerSet.value)
  }
}

const setSelectedProduct = (ext) => {
  if (selectedExtension.value) {
    selectedProductModel.value = tldList.value.find((product) => product.domainTldSettingInfo.tld === ext)
  } else {
    selectedProductModel.value = null
  }
}

const loadSuggestedTlds = async () => {
  suggestedTldsLoading.value = true
  for (const tld of suggestedTlds.value) {
    if (tld === selectedExtension.value) {
      continue
    }
    const checkDomain = await api
      .service('domains')
      .service.check({ domain: domainName.value, tld })
      .catch(() => {
        return null
      })
    if (checkDomain !== null) {
      freeSuggestedTlds.value.push({
        tld: tld,
        isDomainFree: checkDomain.domainAvailable,
        isDomainTransferable: checkDomain.domainTransferable,
      })
    }
  }
  suggestedTldsLoading.value = false
}

const searchDomain = async () => {
  searchDomainLoading.value = true
  freeSuggestedTlds.value = []
  isDomainFree.value = null
  isDomainTransferable.value = null
  transferCode.value = null
  domainName.value = domainNameInput.value

  await nextTick()

  const checkDomain = await api
    .service('domains')
    .service.check({ domain: domainName.value, tld: selectedExtension.value })
    .catch(() => {
      return null
    })
  if (checkDomain !== null) {
    loadSuggestedTlds()
  }

  isDomainFree.value = checkDomain.domainAvailable
  isDomainTransferable.value = checkDomain.domainTransferable
  searchDomainLoading.value = false
}

const setNewSelectedExtension = async (tldInfo, index) => {
  preventSelectedExtensionWatcher.value = true
  freeSuggestedTlds.value[index] = {
    tld: selectedExtension.value,
    isDomainFree: isDomainFree.value,
    isDomainTransferable: isDomainTransferable.value,
  }
  selectedExtension.value = tldInfo.tld
  isDomainFree.value = tldInfo.isDomainFree
  isDomainTransferable.value = tldInfo.isDomainTransferable
  transferCode.value = null
  await nextTick()
  preventSelectedExtensionWatcher.value = false
  $q.notify({
    type: 'positive',
    progress: true,
    message: t('Customer.Register-domain.Selected domain is changed'),
    html: true,
  })
}

const getFilters = async (only = null) => {
  const filtersQuery = {
    only: ['products'],
  }

  if (only) {
    filtersQuery.only = only
  }

  const filters = await api
    .service(itemsService)
    .filters(filtersQuery)
    .catch(() => {
      return null
    })

  if (!filters) {
    return
  }

  if (filters['products']) {
    tldList.value = filters['products']
  }
}

// Watchers
watch(itemsPerSet, () => {
  if (currentSet.value > maxSet.value) {
    currentSet.value = maxSet.value
  }
})

watch(
  selectedExtension,
  (newExtension, oldExtension) => {
    if (!preventSelectedExtensionWatcher.value && typeof oldExtension !== 'undefined' && oldExtension !== newExtension) {
      freeSuggestedTlds.value = []
      isDomainFree.value = null
      isDomainTransferable.value = null
      transferCode.value = null
    }

    showCurrentSet(newExtension)
    setSelectedProduct(newExtension)
  },
  {
    immediate: true,
  },
)

watch(domainNameInput, (newVal, oldVal) => {
  if (newVal !== newVal.toLowerCase()) {
    domainNameInput.value = newVal.toLowerCase()
  }
})

// Lifecycle hooks
onBeforeMount(async () => {
  domainNameInput.value = domainName.value

  await getFilters()

  showCurrentSet(selectedExtension.value)
  setSelectedProduct(selectedExtension.value)
})
</script>

<template>
  <q-spinner color="primary" size="35px" v-if="tldList.length === 0" />
  <template v-else>
    <!-- Arrows -->
    <div class="flex justify-between items-center q-mb-md">
      <div class="q-mx-sm">
        <q-btn color="primary" icon="chevron_left" @click="prevSet" :disable="currentSet === 0" />
      </div>
      <div class="row justify-center items-center q-px-md" v-if="$q.screen.gt.md">
        <q-icon
          v-for="(bullet, index) in totalSets"
          :key="index"
          name="fiber_manual_record"
          class="q-mx-xs cursor-pointer"
          :class="[currentSet === index ? 'text-primary' : 'text-grey-5', currentSet === index ? 'font-size-rem-14' : 'font-size-rem-11']"
          @click="setCurrentSet(index)"
        />
      </div>
      <div class="q-ml-sm">
        <q-btn color="primary" icon-right="chevron_right" @click="nextSet" :disable="currentSet >= maxSet" />
      </div>
    </div>

    <!-- Cards with extensions -->
    <div class="row q-ma-none">
      <div class="col-12 col-sm-6 col-md-4 col-lg-2" v-for="(product, index) in visibleProducts" :key="index">
        <q-card class="rounded-borders q-ml-sm" :class="{ 'border-primary': product.domainTldSettingInfo.tld === selectedExtension }" bordered flat>
          <q-card-section class="text-center q-px-none">
            <q-badge v-if="product.domainTldSettingInfo.tld === 'nl'" floating class="q-pa-sm">
              {{ $t('Customer.Register-domain.Most chosen') }}
            </q-badge>
            <div class="font-size-rem-12 q-mt-md">
              <q-icon name="o_check" class="text-primary q-mr-sm" size="22px" v-if="product.domainTldSettingInfo.tld === selectedExtension" />
              {{ product.domainTldSettingInfo.tld }}
            </div>
            <div class="q-mt-md text-primary font-size-rem-20">
              {{
                numberFormat(
                  useUserStore().currentRegionalSettings.lang,
                  getProductSalesPriceWithDiscount(null, contract, getProductSalesPrice(product.salesPrices, 'year', 1), product._id),
                  true,
                )
              }}
            </div>
            <div class="text-primary q-mt-sm">{{ $t('Customer.Register-domain.per year') }}</div>
            <div class="text-primary">
              &nbsp;
              <template v-if="getProductDiscount(null, contract, getProductSalesPrice(product.salesPrices, 'year', 1), product._id)">
                {{ $t('Customer.Register-domain.incl') }}
                {{
                  numberFormat(
                    useUserStore().currentRegionalSettings.lang,
                    getProductDiscount(null, contract, getProductSalesPrice(product.salesPrices, 'year', 1), product._id),
                    true,
                  )
                }}
                {{ $t('Customer.Register-domain.discount') }}
              </template>
            </div>
            <q-btn
              color="primary"
              class="q-mt-md q-px-md"
              dense
              :label="$t('Customer.Register-domain.Choose')"
              :disable="product.domainTldSettingInfo.tld === selectedExtension"
              @click="selectedExtension = product.domainTldSettingInfo.tld"
            />
          </q-card-section>
        </q-card>
      </div>
    </div>

    <!-- Search for domain -->
    <q-card class="rounded-borders q-ml-sm q-mt-sm" bordered flat>
      <q-card-section class="flex column items-center">
        <div class="q-mb-md" :class="{ 'font-size-rem-12': $q.screen.gt.sm }">
          {{ $t('Customer.Register-domain.Check availability') }}
        </div>
        <div class="flex no-wrap full-width justify-center">
          <q-field class="remove-bottom-padding full-width max-width-px-700 q-pb-none" dense>
            <q-input
              v-model="domainNameInput"
              class="remove-bottom-padding full-width text-lowercase"
              :placeholder="$t('Customer.Register-domain.domain')"
              dense
              :error="!isValidDomain"
            >
              <template #prepend v-if="$q.screen.gt.xs">
                <div class="full-height content-center q-px-sm font-size-rem-10">www.</div>
              </template>
            </q-input>
            <q-select v-model="selectedExtension" :options="extensionOptions" class="remove-bottom-padding extension-width" emit-value dense>
              <template #selected>
                <div class="q-pl-sm" :class="{ 'text-grey-6': !selectedExtension }">
                  {{ selectedExtension ? '.' + selectedExtension : '.' + $t('Customer.Register-domain.extension') }}
                </div>
              </template>
            </q-select>
          </q-field>
          <q-btn
            flat
            icon="search"
            class="q-ml-sm"
            :disable="!domainNameInput || !selectedExtension || !isValidDomain"
            :loading="searchDomainLoading"
            @click="searchDomain"
          />
        </div>
      </q-card-section>
    </q-card>

    <!-- Domain status -->
    <div class="font-size-rem-12 text-center q-mt-md q-mb-sm" v-if="isDomainFree !== null || searchDomainLoading">
      {{ $t('Customer.Register-domain.Selected domain') }}
    </div>
    <div class="text-center q-my-md" v-if="searchDomainLoading">
      <q-spinner color="primary" size="35px" />
    </div>
    <q-card class="rounded-borders q-ml-sm q-mt-sm" bordered flat v-if="isDomainFree !== null && !searchDomainLoading">
      <q-card-section class="bg-grey-1">
        <q-badge floating class="q-pa-sm" :class="{ 'bg-positive': isDomainFree, 'bg-negative': !isDomainFree }"
          >{{ isDomainFree ? $t('Customer.Register-domain.Available') : $t('Customer.Register-domain.Taken') }}
        </q-badge>
        <div class="flex column">
          <div class="font-size-rem-12">{{ domainName }}.{{ selectedExtension }}</div>
          <div class="flex items-center q-mt-md">
            <q-icon :name="isDomainFree ? 'o_done' : 'o_clear'" :class="{ 'text-positive': isDomainFree, 'text-negative': !isDomainFree }" size="xs" />
            <span class="q-ml-sm">{{
              isDomainFree ? $t('Customer.Register-domain.Domain is available') : $t('Customer.Register-domain.Domain is taken')
            }}</span>
          </div>
          <div class="q-mt-md" v-if="!isDomainFree && isDomainTransferable">
            {{ $t('Customer.Register-domain.Is the domain yours, and do you have a transfer code') }}
          </div>
          <div class="q-mt-md" v-else-if="!isDomainFree && !isDomainTransferable">
            {{ $t('Customer.Register-domain.The domain cannot be transferred') }}
          </div>
          <q-input
            v-if="!isDomainFree && isDomainTransferable"
            v-model="transferCode"
            outlined
            class="remove-bottom-padding full-width max-width-px-300 q-mt-md"
            :label="$t('Customer.Register-domain.Transfer code')"
            dense
          />
        </div>
      </q-card-section>
    </q-card>

    <!-- Suggestions -->
    <div class="font-size-rem-12 text-center q-mt-md q-mb-sm" v-if="freeSuggestedTlds.length > 0 || suggestedTldsLoading">
      {{ $t('Customer.Register-domain.Suggestions') }}
    </div>
    <div class="text-center q-my-md" v-if="suggestedTldsLoading">
      <q-spinner color="primary" size="35px" />
    </div>
    <template v-if="freeSuggestedTlds.length > 0 && !suggestedTldsLoading">
      <q-card class="rounded-borders q-ml-sm q-mt-sm" bordered flat v-for="(tldInfo, index) in freeSuggestedTlds" :key="index">
        <q-card-section>
          <div class="row">
            <div class="col column">
              <div class="font-size-rem-12">{{ domainName }}.{{ tldInfo.tld }}</div>
              <div class="flex items-center q-mt-md">
                <q-icon
                  :name="tldInfo.isDomainFree ? 'o_done' : 'o_clear'"
                  :class="{ 'text-positive': tldInfo.isDomainFree, 'text-negative': !tldInfo.isDomainFree }"
                  size="xs"
                />
                <span class="q-ml-sm">{{
                  tldInfo.isDomainFree ? $t('Customer.Register-domain.Domain is available') : $t('Customer.Register-domain.Domain is taken')
                }}</span>
              </div>
            </div>
            <div class="col-auto flex items-center justify-end">
              <q-btn color="primary" class="q-px-md" dense :label="$t('Customer.Register-domain.Choose')" @click="setNewSelectedExtension(tldInfo, index)" />
            </div>
          </div>
        </q-card-section>
      </q-card>
    </template>
  </template>
</template>

<style scoped>
.extension-width {
  width: 110px;
}
</style>
